import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import {
	dropIncidentsQueryVariables,
	startLoadingIncidents,
} from '@app/pages/cmms/components/incident/state/incidents.actions';
import {
	allIncidents,
	incidentAuPagination,
	incidentsIsLoading,
} from '@app/pages/cmms/components/incident/state/incidents.selectors';
import { startLoadingRequests } from '@app/pages/cmms/components/request/state/requests.action';
import {
	allRequests,
	isLoadingRequests,
	requestAuPagination,
} from '@app/pages/cmms/components/request/state/requests.selectors';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { Store } from '@ngrx/store';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { AttachedEntityCardComponent } from '@app/shared/entity-workspace-components/attached-entity-card/attached-entity-card.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { AttachedEntityService } from '@app/shared/services/attach-entity.service';
import { MatIconModule } from '@angular/material/icon';
import { NewRightSidebarService } from '@app/layout/new-right-sidebar/services/new-right-sidebar/new-right-sidebar.service';
import { AuPaginatorComponent } from '@app/shared/ui-components/au-paginator/au-paginator-component/au-paginator.component';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@app/shared/ui-components/au-paginator/pagination-page.info';
import {
	CMMSLoadingFacade,
	CMMSQueryFilters,
} from '@app/pages/cmms/services/cmms-loading.facade';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { CmmsSortService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-sort.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Observable, tap } from 'rxjs';
import { SearchBarComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/search-bar/search-bar.component';

@Component({
	selector: 'attach-existing-entities',
	imports: [
		NgxTolgeeModule,
		SearchBarComponent,
		AttachedEntityCardComponent,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatIconModule,
		AuPaginatorComponent,
	],
	providers: [CMMSLoadingFacade, CmmsFiltersService, CmmsSortService],
	templateUrl: './attach-existing-entities.component.html',
	styleUrl: './attach-existing-entities.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachExistingEntitiesComponent implements OnInit, OnDestroy {
	private readonly router = inject(ActivatedRoute);
	private readonly store$ = inject(Store);
	private readonly attachedEntityService = inject(AttachedEntityService);
	private readonly rightSidebarService = inject(NewRightSidebarService);
	private readonly loadingFacade = inject(CMMSLoadingFacade);
	private readonly destroyRef = inject(DestroyRef);
	title = signal<string>('');
	entityType = signal<'incident' | 'request'>('incident');

	selectedEntityPks = signal<(string | null)[]>([]);
	removedEntities = signal<(IIncident | IRequest | null)[]>([]);

	private readonly incidents = this.store$.selectSignal(allIncidents);
	private readonly requests = this.store$.selectSignal(allRequests);

	isLoadingIncidents = this.store$.selectSignal(incidentsIsLoading);
	isLoadingRequests = this.store$.selectSignal(isLoadingRequests);

	isLoadingResults = computed(() => {
		switch (this.entityType()) {
			case 'incident':
				return this.isLoadingIncidents();
			case 'request':
				return this.isLoadingRequests();
		}
	});

	readonly currentEntity = computed<IIncident[] | IRequest[]>(() => {
		switch (this.entityType()) {
			case 'incident':
				return this.incidents().incidents;
			case 'request':
				return this.requests().requests;
		}
	});

	readonly djangoPaginationData = computed<IDjangoPaginationData>(() => {
		switch (this.entityType()) {
			case 'incident':
				return this.incidents().incidentsPagination;
			case 'request':
				return this.requests().requestPagination;
		}
	});

	ngOnInit(): void {
		this.router.url.subscribe((segments: UrlSegment[]) => {
			if (segments.length > 0) {
				this.title.set(`add_existing_${segments[0].path}`);
				this.entityType.set(segments[0].path as 'incident' | 'request');
				switch (segments[0].path) {
					case 'incident':
						this.subscribeToEntitiesQuery();
						this.selectedEntityPks.set(
							this.attachedEntityService
								.workorderData()
								?.incidentWorkOrders.edges.map(edge => edge.node.pk) ?? []
						);
						break;
					case 'request':
						this.subscribeToEntitiesQuery();
						this.selectedEntityPks.set(
							this.attachedEntityService
								.workorderData()
								?.requestWorkOrders.edges.map(edge => edge.node.pk) ?? []
						);
						break;
				}
			}
		});
	}

	selectionChanged(entity: [boolean, IIncident | IRequest]): void {
		this.selectedEntityPks.set(
			entity[0]
				? [...this.selectedEntityPks(), entity[1].pk]
				: this.selectedEntityPks().filter(e => e !== entity[1].pk)
		);

		if (!entity[0]) {
			this.removedEntities.set([...this.removedEntities(), entity[1]]);
		}
	}

	clearSupport() {
		this.rightSidebarService.closeAndClearSupport();
	}

	public paginationUpdateHandler(pagination: IAuPaginationData) {
		this.loadingFacade.updatePagination(pagination);
	}

	private subscribeToEntitiesQuery() {
		const filter$ = this.loadingFacade
			.getFilters({
				locationIdsField: 'locations',
				searchTextField: 'name',
			})
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => {
					if (this.entityType() === 'incident') {
						this.loadingFacade.resetPagination(incidentAuPagination);
					}
					if (this.entityType() === 'request') {
						this.loadingFacade.resetPagination(requestAuPagination);
					}
				})
			);

		const pagination$ = this.loadingFacade.getPagination(requestAuPagination);
		this.startLoadingEntities(filter$, pagination$);
	}

	private startLoadingEntities(
		filter$: Observable<CMMSQueryFilters>,
		pagination$: Observable<IAuPaginationData>
	) {
		this.loadingFacade
			.getQuery(filter$, pagination$)
			.pipe(
				filter(({ variables }) => !variables.isCalendarQuery),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(({ variables }) => {
				if (this.entityType() === 'incident') {
					this.store$.dispatch(startLoadingIncidents({ variables }));
					return;
				}
				if (this.entityType() === 'request') {
					this.store$.dispatch(startLoadingRequests({ variables }));
				}
			});
	}

	ngOnDestroy() {
		if (this.entityType() === 'incident') {
			this.store$.dispatch(startLoadingIncidents({ variables: {} }));
			if (
				JSON.stringify(this.selectedEntityPks()) !==
				JSON.stringify(
					this.attachedEntityService
						.workorderData()
						?.incidentWorkOrders.edges.map(edge => edge.node.pk)
				)
			) {
				this.attachedEntityService.linkEntityToWorkorder(
					'setIncidents',
					this.selectedEntityPks()
				);
			}
		} else if (this.entityType() === 'request') {
			this.store$.dispatch(startLoadingRequests({ variables: {} }));
			if (
				JSON.stringify(this.selectedEntityPks()) !==
				JSON.stringify(
					this.attachedEntityService
						.workorderData()
						?.requestWorkOrders.edges.map(edge => edge.node.pk)
				)
			) {
				this.attachedEntityService.linkEntityToWorkorder(
					'setRequests',
					this.selectedEntityPks()
				);
			}
		}

		this.store$.dispatch(dropIncidentsQueryVariables());
	}
}
